/**
 * Editor Store (Zustand)
 *
 * Owns pipeline/editor state only. No execution state.
 */

import { create } from 'zustand';

import { ToolRegistry } from '@/engine/ToolRegistry';
import type { IPipelineStep } from '@/interfaces/IPipeline';
import type { ISemanticType } from '@/interfaces/ISemanticType';
import type { ISavedPipeline } from '@/interfaces/IStorage';
import { getDefaultConfig } from '@/interfaces/ITool';
import {
  generatePipelineDescription,
  generatePipelineName,
  reorderVisualizerStepsRuntime,
} from '@/lib/pipelineUtils';
import { generateId } from '@/lib/utils';

/**
 * Find the insertion index for a new pipeline step.
 * - Visualizers: `position === 'head'` → end of leading visualizer block;
 *   otherwise append to the end.
 * - Transforms: insert before trailing visualizer block (existing behaviour).
 */
function findInsertionIndex(
  pipeline: IPipelineStep[],
  newToolId: string,
  position?: 'head' | 'tail',
): number {
  const newTool = ToolRegistry.get(newToolId);

  if (newTool?.type === 'visualizer') {
    if (position === 'head') return findHeadInsertionIndex(pipeline);
    return pipeline.length;
  }

  // Transform: insert before trailing visualizers
  let idx = pipeline.length;
  for (let i = pipeline.length - 1; i >= 0; i--) {
    const stepTool = ToolRegistry.get(pipeline[i].toolId);
    if (stepTool?.type === 'visualizer') {
      idx = i;
    } else {
      break;
    }
  }
  return idx;
}

/** Find insertion index at the end of the leading visualizer block. */
function findHeadInsertionIndex(pipeline: IPipelineStep[]): number {
  for (let i = 0; i < pipeline.length; i++) {
    const stepTool = ToolRegistry.get(pipeline[i].toolId);
    if (stepTool?.type !== 'visualizer') return i;
  }
  return pipeline.length;
}

interface EditorState {
  // ─────────────────────────────────────────────────────────────
  // Data
  // ─────────────────────────────────────────────────────────────
  /** User's input data */
  input: string;
  /** Final output data */
  output: string;
  /** Detected input type */
  inputType: ISemanticType | null;
  /** Detected output type */
  outputType: ISemanticType | null;
  /** Whether input type is auto-detected or manually set by the user */
  inputTypeMode: 'auto' | 'manual';
  /** MIME type of the current input (typically from imported files) */
  inputMimeType: string | null;
  /** Current pipeline steps */
  pipeline: IPipelineStep[];
  /** Name of the currently loaded/active pipeline (shown in UI) */
  activePipelineName: string | null;
  /** Description of the currently loaded/active pipeline (shown in UI) */
  activePipelineDescription: string | null;
  /** Whether the pipeline name was auto-generated (vs user-set) */
  activePipelineNameIsAuto: boolean;
  /** Whether the pipeline description was auto-generated (vs user-set) */
  activePipelineDescriptionIsAuto: boolean;
  /** ID of the currently loaded/active pipeline (null when unsaved/new) */
  activePipelineId: string | null;
  /** Pipeline-scoped variables usable as {{var}} in inputs/configs */
  pipelineVariables: Record<string, string>;
  /** Selected step ID (settings panel) */
  selectedStepId: string | null;
  /** Currently active tool ID (toolbar selection) */
  activeToolId: string | null;

  // ─────────────────────────────────────────────────────────────
  // Actions
  // ─────────────────────────────────────────────────────────────
  /** Set input data */
  setInput: (data: string) => void;
  /** Set output data */
  setOutput: (data: string) => void;
  /** Set detected input type */
  setInputType: (type: ISemanticType | null) => void;
  /** Set detected output type */
  setOutputType: (type: ISemanticType | null) => void;
  /** Set input type mode */
  setInputTypeMode: (mode: 'auto' | 'manual') => void;
  /** Set MIME type of the current input */
  setInputMimeType: (mimeType: string | null) => void;
  /** Set selected step */
  setSelectedStepId: (stepId: string | null) => void;
  /** Set active tool */
  setActiveToolId: (toolId: string | null) => void;
  /** Add a tool to the pipeline */
  addTool: (toolId: string, position?: 'head' | 'tail', panelRole?: 'input' | 'output') => void;
  /** Add a tool at a specific index in the pipeline (0 = beginning) */
  insertToolAt: (toolId: string, index: number, panelRole?: 'input' | 'output') => void;
  /** Add a tool to the pipeline with an explicit config snapshot (e.g. from a preset) */
  addToolWithConfig: (toolId: string, config: Record<string, unknown>, label?: string) => void;
  /** Remove a tool from the pipeline */
  removeTool: (stepId: string) => void;
  /** Update tool configuration */
  updateToolConfig: (stepId: string, config: Record<string, unknown>) => void;
  /** Update a step label (user-friendly name) */
  setStepLabel: (stepId: string, label: string) => void;
  /** Toggle tool enabled state */
  toggleToolEnabled: (stepId: string) => void;
  /** Reorder pipeline steps */
  reorderPipeline: (startIndex: number, endIndex: number) => void;
  /** Clear the pipeline */
  clearPipeline: () => void;
  /** Set active pipeline name */
  setActivePipelineName: (name: string | null) => void;
  /** Set active pipeline description */
  setActivePipelineDescription: (description: string | null) => void;
  /** Set active pipeline id */
  setActivePipelineId: (id: string | null) => void;
  /** Set all pipeline variables */
  setPipelineVariables: (vars: Record<string, string>) => void;
  /** Set/update a single pipeline variable */
  setPipelineVariable: (key: string, value: string) => void;
  /** Remove a pipeline variable */
  removePipelineVariable: (key: string) => void;
  /** Load a saved pipeline into editor state, returning reordered steps. */
  loadPipeline: (pipeline: ISavedPipeline) => IPipelineStep[];
}

/**
 * Returns auto-name/description fields when appropriate.
 * Only overwrites if the current value is auto-generated (not user-set).
 */
function autoNameFields(
  nextPipeline: IPipelineStep[],
  currentState: Pick<EditorState, 'activePipelineNameIsAuto' | 'activePipelineDescriptionIsAuto'>
): Partial<EditorState> {
  const patch: Partial<EditorState> = {};
  if (currentState.activePipelineNameIsAuto) {
    patch.activePipelineName = generatePipelineName(nextPipeline);
  }
  if (currentState.activePipelineDescriptionIsAuto) {
    patch.activePipelineDescription = generatePipelineDescription(nextPipeline);
  }
  return patch;
}

export const useEditorStore = create<EditorState>((set) => ({
  input: '',
  output: '',
  inputType: null,
  outputType: null,
  inputTypeMode: 'auto',
  inputMimeType: null,
  pipeline: [],
  activePipelineName: null,
  activePipelineDescription: null,
  activePipelineNameIsAuto: true,
  activePipelineDescriptionIsAuto: true,
  activePipelineId: null,
  pipelineVariables: {},
  selectedStepId: null,
  activeToolId: null,

  setInput: (data) => {
    set({ input: data, output: '', outputType: null });
  },

  setOutput: (data) => {
    set({ output: data });
  },

  setInputType: (type) => {
    set({ inputType: type });
  },

  setOutputType: (type) => {
    set({ outputType: type });
  },

  setInputTypeMode: (mode) => {
    set({ inputTypeMode: mode });
  },

  setInputMimeType: (mimeType) => {
    set({ inputMimeType: mimeType });
  },

  setSelectedStepId: (stepId) => {
    set({ selectedStepId: stepId });
  },

  setActiveToolId: (toolId) => {
    set({ activeToolId: toolId });
  },

  addTool: (toolId, position, panelRole) => {
    const tool = ToolRegistry.get(toolId);
    if (!tool) {
      console.error(`Tool "${toolId}" not found`);
      return;
    }

    // Visualizers default panelRole based on position
    const effectivePanelRole = panelRole
      ?? (tool.type === 'visualizer' ? (position === 'head' ? 'input' : 'output') : undefined);

    const newStep: IPipelineStep = {
      id: generateId(),
      toolId,
      config: getDefaultConfig(tool),
      isEnabled: true,
      ...(effectivePanelRole ? { panelRole: effectivePanelRole } : {}),
    };

    set((state) => {
      const insertIdx = findInsertionIndex(state.pipeline, toolId, position);
      const nextPipeline = [...state.pipeline];
      nextPipeline.splice(insertIdx, 0, newStep);
      return {
        pipeline: nextPipeline,
        output: '',
        outputType: null,
        ...autoNameFields(nextPipeline, state),
      };
    });
  },

  insertToolAt: (toolId, index, panelRole) => {
    const tool = ToolRegistry.get(toolId);
    if (!tool) {
      console.error(`Tool "${toolId}" not found`);
      return;
    }

    // Visualizers default panelRole based on insertion position
    const effectivePanelRole = panelRole
      ?? (tool.type === 'visualizer' ? (index === 0 ? 'input' : 'output') : undefined);

    const newStep: IPipelineStep = {
      id: generateId(),
      toolId,
      config: getDefaultConfig(tool),
      isEnabled: true,
      ...(effectivePanelRole ? { panelRole: effectivePanelRole } : {}),
    };

    set((state) => {
      const nextPipeline = [...state.pipeline];
      const clampedIndex = Math.max(0, Math.min(index, nextPipeline.length));
      nextPipeline.splice(clampedIndex, 0, newStep);
      return {
        pipeline: nextPipeline,
        output: '',
        outputType: null,
        ...autoNameFields(nextPipeline, state),
      };
    });
  },

  addToolWithConfig: (toolId, config, label) => {
    const tool = ToolRegistry.get(toolId);
    if (!tool) {
      console.error(`Tool "${toolId}" not found`);
      return;
    }

    const nextLabel = label?.trim();
    const newStep: IPipelineStep = {
      id: generateId(),
      toolId,
      label: nextLabel ? nextLabel : undefined,
      config: { ...(config ?? getDefaultConfig(tool)) },
      isEnabled: true,
    };

    set((state) => {
      const insertIdx = findInsertionIndex(state.pipeline, toolId);
      const nextPipeline = [...state.pipeline];
      nextPipeline.splice(insertIdx, 0, newStep);
      return {
        pipeline: nextPipeline,
        output: '',
        outputType: null,
        ...autoNameFields(nextPipeline, state),
      };
    });
  },

  removeTool: (stepId) => {
    set((state) => {
      const nextPipeline = state.pipeline.filter((step) => step.id !== stepId);
      return {
        pipeline: nextPipeline,
        output: '',
        outputType: null,
        ...autoNameFields(nextPipeline, state),
      };
    });
  },

  updateToolConfig: (stepId, config) => {
    set((state) => ({
      pipeline: state.pipeline.map((step) =>
        step.id === stepId ? { ...step, config: { ...step.config, ...config } } : step
      ),
      output: '',
      outputType: null,
    }));
  },

  setStepLabel: (stepId, label) => {
    const nextLabel = label.trim();
    set((state) => ({
      pipeline: state.pipeline.map((step) =>
        step.id === stepId ? { ...step, label: nextLabel ? nextLabel : undefined } : step
      ),
    }));
  },

  toggleToolEnabled: (stepId) => {
    set((state) => ({
      pipeline: state.pipeline.map((step) =>
        step.id === stepId ? { ...step, isEnabled: !step.isEnabled } : step
      ),
      output: '',
      outputType: null,
    }));
  },

  reorderPipeline: (startIndex, endIndex) => {
    set((state) => {
      const newPipeline = [...state.pipeline];
      const [removed] = newPipeline.splice(startIndex, 1);
      newPipeline.splice(endIndex, 0, removed);

      // Guard: prevent a visualizer from landing in the middle of non-visualizer steps.
      // A visualizer is valid only in the head zone (all predecessors are visualizers)
      // or the tail zone (all successors are visualizers).
      const movedTool = ToolRegistry.get(removed.toolId);
      if (movedTool?.type === 'visualizer') {
        const allBeforeAreViz = newPipeline.slice(0, endIndex).every((s) => {
          const t = ToolRegistry.get(s.toolId);
          return t?.type === 'visualizer';
        });
        const allAfterAreViz = newPipeline.slice(endIndex + 1).every((s) => {
          const t = ToolRegistry.get(s.toolId);
          return t?.type === 'visualizer';
        });
        if (!allBeforeAreViz && !allAfterAreViz) {
          // Invalid position — abort the move
          return {};
        }
      }

      return {
        pipeline: newPipeline,
        output: '',
        outputType: null,
        ...autoNameFields(newPipeline, state),
      };
    });
  },

  clearPipeline: () => {
    set({
      pipeline: [],
      output: '',
      outputType: null,
      activePipelineName: null,
      activePipelineDescription: null,
      activePipelineNameIsAuto: true,
      activePipelineDescriptionIsAuto: true,
      activePipelineId: null,
      pipelineVariables: {},
    });
  },

  setActivePipelineName: (name) => {
    set({ activePipelineName: name, activePipelineNameIsAuto: false });
  },

  setActivePipelineDescription: (description) => {
    set({ activePipelineDescription: description, activePipelineDescriptionIsAuto: false });
  },

  setActivePipelineId: (id) => {
    set({ activePipelineId: id });
  },

  setPipelineVariables: (vars) => {
    set({ pipelineVariables: { ...(vars ?? {}) } });
  },

  setPipelineVariable: (key, value) => {
    set((state) => ({
      pipelineVariables: {
        ...state.pipelineVariables,
        [key]: value,
      },
    }));
  },

  removePipelineVariable: (key) => {
    set((state) => {
      const next = { ...state.pipelineVariables };
      delete next[key];
      return { pipelineVariables: next };
    });
  },

  loadPipeline: (pipeline) => {
    const hydratedSteps: IPipelineStep[] = pipeline.steps.map((s) => ({
      id: generateId(),
      toolId: s.toolId,
      label: s.label,
      config: s.config ?? {},
      isEnabled: s.isEnabled ?? true,
      ...(s.panelRole ? { panelRole: s.panelRole } : {}),
    }));

    // Defensive reorder: move mid-pipeline visualizers to tail
    const reordered = reorderVisualizerStepsRuntime(hydratedSteps);

    const updates: Partial<EditorState> = {
      pipeline: reordered,
      activePipelineName: pipeline.name,
      activePipelineDescription: pipeline.description ?? null,
      activePipelineNameIsAuto: false,
      activePipelineDescriptionIsAuto: !pipeline.description,
      activePipelineId: pipeline.id,
      pipelineVariables: pipeline.variables ?? {},
      output: '',
      outputType: null,
    };

    if (pipeline.inputData) {
      updates.input = pipeline.inputData;
    }

    set(updates);
    return reordered;
  },
}));
